import Icon from "@/components/Icon"
import { getIncomeBandDisplayText, MarriagePerson, type MarriageTaxYear } from "@/models"
import { intlCurrencyFormat } from "@/utils/currency"
import { faCheckCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { defineComponent, type ReactiveComponent } from "vue-utils"
import { useEditingMarriageClaimState } from "../state"
import { getIcon } from "./utils"

const formSectionStyling = css`
	display: grid;
	align-items: center;
	grid-template-columns: repeat(2, 1fr);
	gap: 0.5rem 2rem;

	h6 {
		margin-bottom: 0;
	}

	div[data-span="2"] {
		grid-column-start: span 2;
	}

	@media (width < 992px) {
		grid-template-columns: 1fr;
	}
`

const EarningsInformation: ReactiveComponent = () => {
	const state = useEditingMarriageClaimState()

	function renderIconResult(year: MarriageTaxYear, field: MarriagePerson) {
		if (state.fullClaim.HighestEarner === MarriagePerson.Me) {
			if (field === MarriagePerson.Me) {
				return getIcon(year.HighEarnerEarningsInRange)
			} else {
				return getIcon(year.LowEarnerEarningsInRange)
			}
		} else if (field === MarriagePerson.Partner) {
			return getIcon(year.HighEarnerEarningsInRange)
		}
		return year.LowEarnerEarningsInRange === true ? <Icon icon={faCheckCircle} /> : <Icon icon={faXmarkCircle} />
	}

	return () => (
		<div class={formSectionStyling}>
			<div class="d-flex gap-1" data-span="2">
				<h6>Highest Earner:</h6>
				<h6 class="fw-bold">
					{state.fullClaim.HighestEarner === MarriagePerson.Me ? `Claimant` : state.fullClaim.HighestEarner}
				</h6>
			</div>
			<div class="d-flex gap-1">
				<h6>Claimant Income Band:</h6>
				<h6 class="fw-bold">
					{state.fullClaim.IncomeBand !== null ? getIncomeBandDisplayText(state.fullClaim.IncomeBand) : `Unknown`}
				</h6>
			</div>
			<div class="d-flex gap-1">
				<h6>Partner Income Band:</h6>
				<h6 class="fw-bold">
					{state.fullClaim.PartnerIncomeBand !== null
						? getIncomeBandDisplayText(state.fullClaim.PartnerIncomeBand)
						: `Unknown`}
				</h6>
			</div>
			{state.fullClaim.TaxYears.length > 0 && state.fullClaim.HighestEarner !== null && (
				<div data-span="2">
					<table
						class={`table small table-striped table-hover grid-table grid-table-bordered`}
						style={{
							gridTemplateColumns: `1fr 1fr 1fr max-content max-content`,
							marginTop: `1rem`,
						}}
					>
						<thead>
							<tr>
								<th>Year</th>
								<th>Bracket Minimum</th>
								<th>Bracket Maximum</th>
								<th>Claimant Earnings in Bracket?</th>
								<th>Partner Earnings in Bracket?</th>
							</tr>
						</thead>
						<tbody>
							{state.fullClaim.TaxYears.map((year) => (
								<tr key={year.Year}>
									<td class="d-flex align-items-center">{year.Year}</td>
									<td class="d-flex align-items-center">
										{intlCurrencyFormat(state.getTaxYearBracket(year.Year).BracketMinimum)}
									</td>
									<td class="d-flex align-items-center">
										{intlCurrencyFormat(state.getTaxYearBracket(year.Year).BracketMaximum)}
									</td>
									<td class="d-flex align-items-center justify-content-center p1" style="font-size: 1.75rem">
										{renderIconResult(year, MarriagePerson.Me)}
									</td>
									<td class="d-flex align-items-center justify-content-center p1" style="font-size: 1.75rem">
										{renderIconResult(year, MarriagePerson.Partner)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	)
}

export default defineComponent(EarningsInformation)
